class ProductModal extends ModalDialog {
  constructor() {
    super();

    this.activeMediaId = null;
    this.sliderAttempts = 0;
    this.maximumSliderAttempts = 40;
  }

  hide() {
    removeTrapFocus();
    super.hide();

    if (typeof window.pauseAllMedia === "function") {
      window.pauseAllMedia();
    }
  }

  show(opener) {
    /*
     * Save the exact Shopify media ID before opening the modal.
     */
    this.activeMediaId = this.getMediaId(opener);

    super.show(opener);

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this;
        const focusElement = this.querySelector(
          ".product-media-modal__dialog"
        );

        if (focusElement) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      { once: true }
    );

    /*
     * Wait one frame so the modal is visible before moving Swiper.
     */
    window.requestAnimationFrame(() => {
      this.showActiveMedia();
    });
  }

  /**
   * Get the complete media identifier:
   * section ID + Shopify media ID.
   */
  getMediaId(opener) {
    if (!opener) return null;

    const directMediaId =
      opener.getAttribute?.("data-media-id");

    if (directMediaId) {
      return directMediaId;
    }

    const openerWithMediaId = opener.closest?.(
      "[data-media-id]"
    );

    if (openerWithMediaId) {
      return openerWithMediaId.getAttribute(
        "data-media-id"
      );
    }

    const modalOpener = opener.closest?.(
      "modal-opener"
    );

    if (modalOpener) {
      return modalOpener.getAttribute(
        "data-media-id"
      );
    }

    return null;
  }

  /**
   * Open the exact modal slide matching the clicked product image.
   */
  showActiveMedia() {
    const mediaId =
      this.activeMediaId ||
      this.getMediaId(this.openedBy);

    if (!mediaId) return;

    const popupSliderElement = this.querySelector(
      ".js-popup-slider"
    );

    const matchingSlide = this.querySelector(
      `[data-media-modal-id="${mediaId}"]`
    );

    if (!popupSliderElement || !matchingSlide) {
      return;
    }

    const modalSlides = Array.from(
      popupSliderElement.querySelectorAll(
        ".swiper-wrapper > .swiper-slide"
      )
    );

    const targetIndex =
      modalSlides.indexOf(matchingSlide);

    if (targetIndex < 0) return;

    const popupSwiper =
      popupSliderElement.swiper;

    /*
     * popupSliderInit may still be initializing.
     * Retry briefly until the Swiper instance exists.
     */
    if (!popupSwiper) {
      this.sliderAttempts += 1;

      if (
        this.sliderAttempts <
        this.maximumSliderAttempts
      ) {
        window.setTimeout(() => {
          this.showActiveMedia();
        }, 50);
      }

      return;
    }

    this.sliderAttempts = 0;

    /*
     * Move to the exact image that the customer clicked.
     */
    if (
      typeof popupSwiper.slideToLoop ===
        "function" &&
      popupSwiper.params?.loop
    ) {
      popupSwiper.slideToLoop(
        targetIndex,
        0,
        false
      );
    } else if (
      typeof popupSwiper.slideTo === "function"
    ) {
      popupSwiper.slideTo(
        targetIndex,
        0,
        false
      );
    }

    popupSwiper.update?.();

    /*
     * Update Swiper after the modal becomes visible.
     */
    window.requestAnimationFrame(() => {
      popupSwiper.update?.();

      if (
        typeof popupSwiper.slideToLoop ===
          "function" &&
        popupSwiper.params?.loop
      ) {
        popupSwiper.slideToLoop(
          targetIndex,
          0,
          false
        );
      } else {
        popupSwiper.slideTo?.(
          targetIndex,
          0,
          false
        );
      }
    });

    this.loadActiveDeferredMedia(
      matchingSlide
    );
  }

  /**
   * Load a video or 3D model only when its slide is opened.
   */
  loadActiveDeferredMedia(activeSlide) {
    if (!activeSlide) return;

    const deferredMedia =
      activeSlide.matches?.(
        "deferred-media, product-model"
      )
        ? activeSlide
        : activeSlide.querySelector(
            "deferred-media, product-model"
          );

    if (!deferredMedia) return;

    const templateHasYouTube =
      deferredMedia
        .querySelector("template")
        ?.content?.querySelector(
          ".js-youtube"
        );

    if (
      templateHasYouTube &&
      typeof deferredMedia.loadContent ===
        "function"
    ) {
      deferredMedia.loadContent();
    }
  }
}

if (
  !customElements.get("product-modal")
) {
  customElements.define(
    "product-modal",
    ProductModal
  );
}